import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { logger } from './logger';

export interface UserTracking {
  userId: string;                    // Reddit user ID after login
  anonymousId?: string;             // Temporary ID before Reddit login
  firstSeenDate: Date;              // When they first visit the app
  trialStartDate: Date;             // When they login with Reddit
  lastActiveDate: Date;             // Last time they were active
  trialEndDate: Date;               // When trial period ends
  totalPostsViewed: number;         // Number of posts viewed
  totalKeywordsSearched: number;    // Number of keywords searched
  subscriptionStatus: 'trial' | 'active' | 'expired';
  deviceInfo: {
    platform: string;
    browser: string;
    os: string;
  };
}

// Generate a unique anonymous ID
const generateAnonymousId = () => {
  return 'anon_' + Math.random().toString(36).substr(2, 9);
};

// Get user identifier (either Reddit ID or anonymous ID)
const getUserIdentifier = () => {
  const token = localStorage.getItem('reddit_token');
  const anonymousId = localStorage.getItem('anonymous_id');
  
  if (token) {
    // If we have a token, get the user ID from it
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub; // Reddit user ID
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }
  
  return anonymousId;
};

// Get headers with user identifier
const getHeaders = () => {
  const token = localStorage.getItem('reddit_token');
  const userId = getUserIdentifier();
  const anonymousId = localStorage.getItem('anonymous_id');
  
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (userId) {
    headers['x-user-id'] = userId;
  }
  
  if (anonymousId) {
    headers['x-anonymous-id'] = anonymousId;
  }
  
  return headers;
};

export const initializeUserTracking = async (isAnonymous: boolean = true) => {
  const token = localStorage.getItem('reddit_token');
  const anonymousId = localStorage.getItem('anonymous_id');
  
  if (!token && !anonymousId) return;

  try {
    await axios.post(`${API_ENDPOINTS.USER_TRACKING}/initialize`, {
      isAnonymous,
      anonymousId,
      deviceInfo: {
        platform: 'web',
        browser: navigator.userAgent,
        os: navigator.platform
      }
    }, { headers: getHeaders() });
  } catch (error) {
    logger.error('Error initializing user tracking:', error);
  }
};

export const trackPostView = async () => {
  try {
    await axios.post(`${API_ENDPOINTS.USER_TRACKING}/post-view`, {}, { headers: getHeaders() });
  } catch (error) {
    logger.error('Error tracking post view:', error);
  }
};

export const trackKeywordSearch = async (keyword: string) => {
  try {
    await axios.post(`${API_ENDPOINTS.USER_TRACKING}/keyword-search`, { keyword }, { headers: getHeaders() });
  } catch (error) {
    logger.error('Error tracking keyword search:', error);
  }
};

export const updateUserAfterLogin = async () => {
  const token = localStorage.getItem('reddit_token');
  const anonymousId = localStorage.getItem('anonymous_id');
  
  if (!token || !anonymousId) return;

  try {
    await axios.post(`${API_ENDPOINTS.USER_TRACKING}/initialize`, {
      isAnonymous: false,
      anonymousId,
      deviceInfo: {
        platform: 'web',
        browser: navigator.userAgent,
        os: navigator.platform
      }
    }, { headers: getHeaders() });
  } catch (error) {
    logger.error('Error updating user after login:', error);
  }
}; 